/* eslint-disable max-len */
/* eslint-disable object-curly-newline */
/* eslint-disable react/prop-types */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/no-array-index-key */
import React, { useContext, useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Plus, X, ChevronDown } from 'lucide-react';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { ChipSelectSearch } from '../../components/muiCustomized/ChipSelectSearchable';
import {
  AccrualSchedule, ApprovalRequiredItem, BalanceRolloverItem, NegativeBalanceItem, PaidItem,
} from '../../constants/offTimePolicy';

const FORM_DEFAULT = {
  name: '',
  teams: [],
  addTeams: true,
  members: [],
  addMembers: true,
  scheduleOfAccrual: '',
  carryoverLimitHours: 16,
  maxTotalAccrualHours: 120,
  allowNegativeBalance: '',
  approvalRequired: '',
  balanceRollover: '',
  paid: '',
  accrualTiers: [{ yearsOfService: 0, accrualRate: 1.0, maxTotalAccrualHours: 80 }],
};

const INPUT = 'w-full box-border h-10 px-3 border border-rule rounded-[8px] text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-brand-500';
const LABEL = 'block text-sm font-medium text-ink-900 mb-1.5';

// Custom DS dropdown that shows each option's text + description (native
// <select> can't render two-line options), preserving all dropdown content.
function DescribedSelect({ label, value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setOpen((o) => !o);
  };

  const selected = value !== '' && value !== undefined ? options.find((o) => o.value === value) : null;

  return (
    <div>
      <span className={LABEL}>{label}</span>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="w-full box-border h-10 px-3 flex items-center justify-between gap-2 border border-rule rounded-[8px] bg-white text-sm focus:outline-none focus:border-brand-500 transition"
        style={{ borderStyle: 'solid' }}
      >
        <span className={`truncate ${selected ? 'text-ink-900' : 'text-ink-500'}`}>{selected ? selected.text : (placeholder || 'Required')}</span>
        <ChevronDown size={16} className={`text-ink-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && rect && createPortal(
        <div
          ref={popRef}
          className="bg-white border border-rule rounded-[8px] overflow-auto"
          style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width, zIndex: 2000, borderStyle: 'solid', boxShadow: '0 8px 24px rgba(14, 30, 64, 0.12)', maxHeight: 300 }}
        >
          {options.map((o) => {
            const isSel = selected && selected.value === o.value;
            return (
              <button
                key={String(o.value)}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={`block w-full text-left px-3 py-2.5 border-0 cursor-pointer transition ${isSel ? 'bg-[#eef1f7]' : 'bg-white hover:bg-surface-page'}`}
              >
                <span className="block text-sm font-semibold text-ink-900">{o.text}</span>
                {o.description ? <span className="block text-xs text-ink-500 mt-0.5" style={{ lineHeight: 1.5 }}>{o.description}</span> : null}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

function TimeOffPolicyModal({ closeModal, getPolicies, teams, members, policy = null }) {
  const { activeWorkspace } = useContext(UserContext);
  const [formData, setFormData] = useState(policy ? {
    ...policy,
    teams: teams.filter((team) => policy.teams.findIndex((val) => val.id === team.id) >= 0),
    members: members.filter((member) => policy.members.findIndex((val) => val.id === member.id) >= 0),
  } : FORM_DEFAULT);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const disableSaveBtn = useMemo(() => !(
    formData.name
    && formData.scheduleOfAccrual
    && typeof formData.allowNegativeBalance === 'boolean'
    && typeof formData.approvalRequired === 'boolean'
    && typeof formData.balanceRollover === 'boolean'
    && typeof formData.paid === 'boolean'
  ) || saving, [formData, saving]);

  useEffect(() => {
    if (formData.scheduleOfAccrual === 'worked_hours' && !policy) {
      setFormData((prev) => ({ ...prev, carryoverLimitHours: 16, maxTotalAccrualHours: 120 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.scheduleOfAccrual]);

  const handleForm = (e) => {
    if (e.target.type === 'checkbox') setFormData({ ...formData, [e.target.name]: e.target.checked });
    else setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const setField = (name) => (v) => setFormData({ ...formData, [name]: v });

  const handleAddOffTimePolicy = async () => {
    setSaving(true);
    setFormErrors({});
    const response = await sendRequest({ method: 'post', url: API.WORKSPACE.createTimeOffPolicy(activeWorkspace.id), data: formData });
    if (response.success) {
      setFormData(FORM_DEFAULT);
      getPolicies();
      closeModal();
    } else {
      setFormErrors(response.data || {});
    }
    setSaving(false);
  };

  const handleUpdateOffTimePolicy = async () => {
    setSaving(true);
    setFormErrors({});
    const response = await sendRequest({ method: 'put', url: API.WORKSPACE.updateOffTimePolicy(activeWorkspace.id, policy.id), data: formData });
    if (response.success) {
      getPolicies();
      closeModal();
    } else {
      setFormErrors(response.data || {});
    }
    setSaving(false);
  };

  const handleSelectAllTeams = () => setFormData({ ...formData, teams });
  const handleSelectAllMembers = () => setFormData({ ...formData, members });
  const addTier = () => setFormData((prev) => ({ ...prev, accrualTiers: [...prev.accrualTiers, { yearsOfService: 0, accrualRate: 1.0, maxTotalAccrualHours: 80 }] }));
  const removeTier = (index) => setFormData((prev) => ({ ...prev, accrualTiers: prev.accrualTiers.filter((_, i) => i !== index) }));
  const updateTier = (index, field, value) => setFormData((prev) => {
    const updatedTiers = [...prev.accrualTiers];
    updatedTiers[index][field] = value;
    return { ...prev, accrualTiers: updatedTiers };
  });

  const scheduleOptions = Object.keys(AccrualSchedule).map((key) => ({ value: key, text: AccrualSchedule[key].text, description: AccrualSchedule[key].description }));

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c">
      <Box style={{ width: 720, maxWidth: 'calc(100% - 32px)', maxHeight: '90vh', overflowY: 'auto', padding: 24, background: '#fff', borderRadius: 12 }}>
        <h5 className="text-lg font-semibold leading-none text-ink-900 mb-5">{policy ? 'Edit time off policy' : 'Add time off policy'}</h5>

        <div className="flex flex-col gap-4">
          <div>
            <span className={LABEL}>Policy Name</span>
            <input
              name="name"
              value={formData.name}
              onChange={handleForm}
              placeholder="e.g. Vacation"
              className={`w-full box-border h-10 px-3 border rounded-[8px] text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none ${formErrors?.name ? 'border-[#CC4525]' : 'border-rule focus:border-brand-500'}`}
              style={{ borderStyle: 'solid' }}
            />
            {formErrors?.name ? <p className="text-xs text-[#CC4525] mt-1.5">{formErrors.name}</p> : null}
          </div>

          <div>
            <ChipSelectSearch redesign id="teams" label="Add Teams" placeholder="Add teams" selectAllBtn count={`${formData.teams.length}/${teams.length}`} selectedMembers={formData.teams} handleChange={handleForm} members={teams} handleSelectAll={handleSelectAllTeams} />
            <label className="flex items-center gap-2.5 cursor-pointer mt-2">
              <input type="checkbox" name="addTeams" checked={formData.addTeams} onChange={handleForm} style={{ width: 16, height: 16, accentColor: '#3d5a8a' }} />
              <span className="text-sm text-ink-700">Automatically add new teams to this policy</span>
            </label>
          </div>

          <div>
            <ChipSelectSearch redesign id="members" label="Add Members" placeholder="Add members" selectAllBtn count={`${formData.members.length}/${members.length}`} selectedMembers={formData.members} handleChange={handleForm} members={members} handleSelectAll={handleSelectAllMembers} />
            <label className="flex items-center gap-2.5 cursor-pointer mt-2">
              <input type="checkbox" name="addMembers" checked={formData.addMembers} onChange={handleForm} style={{ width: 16, height: 16, accentColor: '#3d5a8a' }} />
              <span className="text-sm text-ink-700">Automatically add new members to this policy</span>
            </label>
          </div>

          <DescribedSelect label="Schedule of Accrual" value={formData.scheduleOfAccrual} onChange={setField('scheduleOfAccrual')} options={scheduleOptions} />

          {formData.scheduleOfAccrual === 'worked_hours' && (
            <div>
              <span className={LABEL}>Accrual Tiers</span>
              <div className="flex flex-col gap-2">
                {formData.accrualTiers.map((tier, index) => (
                  <div key={index} className="flex items-end gap-2">
                    <div className="flex-1 min-w-0">
                      <span className="block text-xs text-ink-500 mb-1">Years of Service (+)</span>
                      <input type="number" step="1" value={tier.yearsOfService} onChange={(e) => updateTier(index, 'yearsOfService', parseFloat(e.target.value))} className={INPUT} style={{ borderStyle: 'solid' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="block text-xs text-ink-500 mb-1">Accrual Rate (per 24h)</span>
                      <input type="number" step="0.25" value={tier.accrualRate} onChange={(e) => updateTier(index, 'accrualRate', parseFloat(e.target.value))} className={INPUT} style={{ borderStyle: 'solid' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="block text-xs text-ink-500 mb-1">Max Accrual (Hours)</span>
                      <input type="number" step="1" value={tier.maxTotalAccrualHours} onChange={(e) => updateTier(index, 'maxTotalAccrualHours', parseFloat(e.target.value))} className={INPUT} style={{ borderStyle: 'solid' }} />
                    </div>
                    {index === 0 ? (
                      <button type="button" aria-label="Add tier" onClick={addTier} className="inline-flex items-center justify-center flex-shrink-0 box-border h-10 w-10 rounded-[8px] border border-rule text-[#458a44] hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}><Plus size={16} /></button>
                    ) : (
                      <button type="button" aria-label="Remove tier" onClick={() => removeTier(index)} className="inline-flex items-center justify-center flex-shrink-0 box-border h-10 w-10 rounded-[8px] border border-rule text-[#CC4525] hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}><X size={16} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {formData.scheduleOfAccrual && formData.scheduleOfAccrual !== 'none' && formData.scheduleOfAccrual !== 'worked_hours' && (
            <div>
              <span className={LABEL}>Annual Accrual Cap (Hours)</span>
              <input type="number" name="maxTotalAccrualHours" value={formData.maxTotalAccrualHours} onChange={handleForm} className={INPUT} style={{ borderStyle: 'solid' }} />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DescribedSelect label="Allow Negative Balances" value={formData.allowNegativeBalance} onChange={setField('allowNegativeBalance')} options={NegativeBalanceItem} />
            <DescribedSelect label="Requires Approval" value={formData.approvalRequired} onChange={setField('approvalRequired')} options={ApprovalRequiredItem} />
            <DescribedSelect label="Balance Rolls Over Annually" value={formData.balanceRollover} onChange={setField('balanceRollover')} options={BalanceRolloverItem} />
            <DescribedSelect label="Paid or Unpaid" value={formData.paid} onChange={setField('paid')} options={PaidItem} />
          </div>

          {formData.balanceRollover === true && (
            <div>
              <span className={LABEL}>Max Carryover Limit (Hours)</span>
              <input type="number" name="carryoverLimitHours" value={formData.carryoverLimitHours} onChange={handleForm} className={INPUT} style={{ borderStyle: 'solid' }} />
            </div>
          )}
        </div>

        <div className="flex justify-start items-center gap-2 mt-6">
          <button type="button" onClick={closeModal} className="inline-flex items-center justify-center box-border h-10 px-4 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition" style={{ borderStyle: 'solid' }}>Cancel</button>
          <button type="button" onClick={policy ? handleUpdateOffTimePolicy : handleAddOffTimePolicy} disabled={disableSaveBtn} className="inline-flex items-center justify-center box-border h-10 px-4 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-40 disabled:cursor-not-allowed transition">
            {/* eslint-disable-next-line no-nested-ternary */}
            {saving ? 'Saving…' : policy ? 'Save Changes' : 'Create Policy'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

TimeOffPolicyModal.propTypes = {
  policy: PropTypes.shape({
    id: PropTypes.number.isRequired,
    teams: PropTypes.arrayOf(PropTypes.shape({})),
    members: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  closeModal: PropTypes.func.isRequired,
  getPolicies: PropTypes.func.isRequired,
  teams: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  members: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
};

TimeOffPolicyModal.defaultProps = { policy: null, teams: [], members: [] };

export default TimeOffPolicyModal;
